import { useEffect, useRef, useState } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog';
import { SelectField } from '@/shared/ui/select';

import { operationKindLabels, runSuiteLabels } from '../field-object-labels';

import { FieldControls } from './field-controls';
import { FieldEmpty, FieldPanelHeading, FieldStatus, FieldTime } from './field-primitives';
import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import './field-objects.css';

import type { FieldPanelProps } from './field-types';
import type { FieldRun, FieldSnapshot } from '@mio/shared';

const suites: FieldRun['suite'][] = [
  'contracts',
  'security',
  'evaluation',
  'acceptance',
  'rollback',
  'demo',
];

function Incident({
  incident,
  busy,
  onAction,
}: Pick<FieldPanelProps, 'busy' | 'onAction'> & { incident: FieldSnapshot['incidents'][number] }) {
  const [text, setText] = useState('');
  const [statusChange, setStatusChange] = useState<{
    value: typeof incident.status;
    expectedStatus: typeof incident.status;
  } | null>(null);
  const status = statusChange?.value ?? incident.status;
  const statusConflict = statusChange !== null && statusChange.expectedStatus !== incident.status;
  return (
    <article
      tabIndex={-1}
      data-object-id={incident.id}
      className="border-line rounded-md border p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">
          {incident.severity} · {incident.title}
        </h3>
        <FieldStatus value={incident.status} />
      </div>
      <ol className="field-incident-timeline">
        {incident.updates.map((update, index) => (
          <li key={`${update.createdAt}-${index}`}>
            <p className="text-fg-muted text-xs">
              <FieldTime value={update.createdAt} />
            </p>
            <p className="whitespace-pre-wrap text-sm">{update.text}</p>
          </li>
        ))}
      </ol>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (statusConflict) return;
          void onAction({
            type: 'incident.update',
            incidentId: incident.id,
            text,
            ...(statusChange
              ? { status: statusChange.value, expectedStatus: statusChange.expectedStatus }
              : {}),
          }).then((sent) => {
            if (sent) {
              setText((draft) => (draft === text ? '' : draft));
              setStatusChange((draft) => (draft === statusChange ? null : draft));
            }
          });
        }}
      >
        <label htmlFor={`incident-${incident.id}`} className={FIELD_LABEL}>
          Containment / recovery update
        </label>
        <textarea
          id={`incident-${incident.id}`}
          className={FIELD_INPUT}
          rows={2}
          maxLength={8000}
          required
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        {statusConflict ? (
          <Alert tone="info">
            This incident is now {incident.status}. Your update is saved here. Select a status again
            to confirm a change, or{' '}
            <button type="button" className="underline" onClick={() => setStatusChange(null)}>
              use the current status
            </button>
            .
          </Alert>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <SelectField
            aria-label="Incident status"
            value={status}
            onValueChange={(nextValue) => {
              const value = nextValue;
              if (value === 'open' || value === 'mitigated' || value === 'resolved')
                setStatusChange(
                  value === incident.status ? null : { value, expectedStatus: incident.status },
                );
            }}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'mitigated', label: 'Mitigated' },
              { value: 'resolved', label: 'Resolved' },
            ]}
          />
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            disabled={busy || !text.trim() || statusConflict}
          >
            Post update
          </Button>
        </div>
      </form>
    </article>
  );
}

export function FieldOperations({
  snapshot,
  busy,
  onAction,
  focusId,
  focusRequest,
  candidateId,
  onAskRelease,
}: FieldPanelProps & {
  focusId?: string | undefined;
  focusRequest?: number;
  candidateId?: string | undefined;
  onAskRelease?: (prId: string) => Promise<boolean>;
}) {
  const [prId, setPrId] = useState('');
  const [suite, setSuite] = useState<FieldRun['suite']>('contracts');
  const [reason, setReason] = useState('');
  const [releasePrId, setReleasePrId] = useState('');
  const [search, setSearch] = useState('');
  const [cancelId, setCancelId] = useState<string | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!candidateId) return;
    setPrId(candidateId);
    setReleasePrId(candidateId);
  }, [candidateId, focusRequest]);
  useEffect(() => {
    if (!focusId) return;
    setSearch('');
    const frame = requestAnimationFrame(() => {
      const element = Array.from(
        panel.current?.querySelectorAll<HTMLElement>('[data-object-id]') ?? [],
      ).find((element) => element.dataset.objectId === focusId);
      element?.focus({ preventScroll: true });
      element?.scrollIntoView?.({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusId, focusRequest]);
  const runs = snapshot.runs
    .filter((run) =>
      `${runSuiteLabels[run.suite]} ${run.revision} ${snapshot.prs.find((pr) => pr.id === run.prId)?.title ?? ''}`
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
    )
    .sort((a, b) => Number(b.status === 'failed') - Number(a.status === 'failed'));
  const operationGroups = [
    {
      title: 'Needs attention & in progress',
      operations: snapshot.operations
        .filter((operation) => operation.status !== 'completed')
        .sort((a, b) => Number(b.status === 'failed') - Number(a.status === 'failed')),
    },
    {
      title: 'Completed operations',
      operations: snapshot.operations.filter((operation) => operation.status === 'completed'),
    },
  ];
  const cancellation = snapshot.operations.find((operation) => operation.id === cancelId);
  const selected = snapshot.prs.find((pr) => pr.id === prId);
  const releaseCandidate = snapshot.prs.find((pr) => pr.id === releasePrId);
  const releaseReady =
    snapshot.release.status === 'ready' &&
    releaseCandidate?.id === snapshot.release.prId &&
    releaseCandidate?.revision === snapshot.release.revision;
  const releaseActive = ['released', 'paused'].includes(snapshot.release.status);
  return (
    <div ref={panel} className="field-object-panel h-full overflow-y-auto">
      <FieldPanelHeading title="Runs & release">
        Checks belong to an exact source revision. Only controller-verified results count as trusted
        execution.
      </FieldPanelHeading>
      <div className="space-y-5 p-5">
        <details className="border-line border-b pb-3">
          <summary className="cursor-pointer text-sm font-semibold">
            Company decisions{' '}
            <span className="text-fg-muted ml-2 font-normal">
              {snapshot.controls?.holds.filter((hold) => hold.status === 'active').length ?? 0}{' '}
              active holds · {snapshot.controls?.acceptances.length ?? 0} acceptance records
            </span>
          </summary>
          <FieldControls snapshot={snapshot} />
        </details>
        {!snapshot.capabilities.observations ? (
          <Alert tone="info">
            Trusted execution is unavailable in this environment. Source analysis and
            learner-uploaded output do not establish passing checks.
          </Alert>
        ) : null}
        <form
          className="border-line space-y-3 rounded-md border p-4"
          onSubmit={(event) => {
            event.preventDefault();
            void onAction({ type: 'run.request', prId, suite });
          }}
        >
          <h3 className="text-base font-semibold">Run checks</h3>
          <div className="field-object-form-grid">
            <div>
              <label htmlFor="run-pr" className={FIELD_LABEL}>
                Candidate
              </label>
              <SelectField
                id="run-pr"
                value={prId}
                onValueChange={(nextValue) => setPrId(nextValue)}
                options={[
                  { value: '', label: 'Select a pull request' },
                  ...(snapshot.prs.map((pr) => ({
                    value: pr.id,
                    label: (
                      <>
                        {pr.title} · {pr.revision.slice(0, 8)}
                      </>
                    ),
                  })) ?? []),
                ]}
              />
            </div>
            <div>
              <label htmlFor="run-suite" className={FIELD_LABEL}>
                Check suite
              </label>
              <SelectField
                id="run-suite"
                value={suite}
                onValueChange={(nextValue) => {
                  const next = suites.find((item) => item === nextValue);
                  if (next) setSuite(next);
                }}
                options={[
                  ...(suites.map((item) => ({ value: item, label: runSuiteLabels[item] })) ?? []),
                ]}
              />
            </div>
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={busy || !selected || !snapshot.capabilities.observations}
          >
            Run checks
          </Button>
        </form>
        <section className="border-line space-y-3 rounded-md border p-4">
          <div className="flex items-center justify-between gap-2" data-field-tour="release">
            <h3 className="text-base font-semibold">Release to the synthetic cohort</h3>
            <FieldStatus value={snapshot.release.status} />
          </div>
          {snapshot.release.revision ? (
            <p className="text-fg-muted text-sm">
              Current release revision{' '}
              <code title={snapshot.release.revision}>{snapshot.release.revision.slice(0, 8)}</code>
            </p>
          ) : null}
          <ul className="text-fg-muted list-disc space-y-1 pl-4 text-sm">
            {snapshot.release.blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
          <p className="text-fg-muted text-sm leading-6">
            Check the selected candidate’s evidence, then ask Amara for a release decision. Only
            Amara can release it to the simulated company.
          </p>
          <div>
            <label htmlFor="release-pr" className={FIELD_LABEL}>
              Release candidate
            </label>
            <SelectField
              id="release-pr"
              value={releasePrId}
              onValueChange={setReleasePrId}
              options={[
                { value: '', label: 'Select a pull request' },
                ...snapshot.prs.map((pr) => ({
                  value: pr.id,
                  label: `${pr.title} · ${pr.revision.slice(0, 8)}`,
                })),
              ]}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || !releaseCandidate || releaseActive}
            onClick={() => void onAction({ type: 'release.request', prId: releasePrId })}
          >
            Check release readiness
          </Button>
          {releaseReady ? (
            <div className="space-y-2">
              <p className="text-fg-muted text-sm leading-6">
                This candidate passed the readiness check. Ask Amara to inspect its current evidence
                and record the release decision. Confirm the status becomes Released before treating
                the handoff as complete.
              </p>
              {onAskRelease ? (
                <Button
                  size="sm"
                  disabled={busy || !snapshot.capabilities.chat}
                  onClick={() => void onAskRelease(releasePrId)}
                >
                  Ask Amara to release
                </Button>
              ) : (
                <p className="text-fg-muted text-sm">
                  Open Amara’s conversation and share this pull request to request release.
                </p>
              )}
            </div>
          ) : null}
          {releaseActive ? (
            <p className="text-fg-muted text-sm">
              A release is already active. Ask Amara to review a replacement or request a rollback
              below.
            </p>
          ) : null}
          {['released', 'paused'].includes(snapshot.release.status) ? (
            <form
              className="space-y-2"
              onSubmit={(event) => {
                event.preventDefault();
                void onAction({ type: 'release.rollback', reason });
              }}
            >
              <label htmlFor="rollback-reason" className={FIELD_LABEL}>
                Rollback reason
              </label>
              <input
                id="rollback-reason"
                className={FIELD_INPUT}
                maxLength={4000}
                required
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
              <Button type="submit" size="sm" disabled={busy || !reason.trim()}>
                Request rollback
              </Button>
            </form>
          ) : null}
        </section>
        <section aria-label="Run history">
          <div className="field-object-collection-heading">
            <h3 data-field-tour="runs">Run history</h3>
            <span>Failures first</span>
          </div>
          <label htmlFor="run-search" className={FIELD_LABEL}>
            Find a run
          </label>
          <input
            id="run-search"
            type="search"
            className={FIELD_INPUT}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search candidate, suite, or revision"
          />
          {!runs.length ? (
            <FieldEmpty
              title={snapshot.runs.length ? 'No runs match your search' : 'No checks have run yet'}
            >
              Select a candidate and a check suite above to collect verification evidence.
            </FieldEmpty>
          ) : (
            runs.map((run) => (
              <article
                key={run.id}
                className="field-object-card"
                tabIndex={-1}
                data-object-id={run.id}
                data-focused={focusId === run.id}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-base font-semibold">{runSuiteLabels[run.suite]}</h4>
                    <p className="text-fg-muted mt-1 text-sm">
                      {snapshot.prs.find((pr) => pr.id === run.prId)?.title ?? 'Candidate'} ·{' '}
                      <code title={run.revision}>{run.revision.slice(0, 8)}</code>
                    </p>
                  </div>
                  <FieldStatus value={run.status} />
                </div>
                <div className="field-object-meta mt-2">
                  <span>
                    {run.trusted
                      ? 'Verified controller result'
                      : 'Not verified by a trusted controller'}
                  </span>
                  <FieldTime value={run.createdAt} />
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6">{run.summary}</p>
                <details className="field-run-checks" open={focusId === run.id}>
                  <summary>
                    {run.checks.length - run.checks.filter((check) => check.passed).length} not
                    passed · {run.checks.filter((check) => check.passed).length} reported passes ·{' '}
                    {run.checks.length} checks
                  </summary>
                  <ul className="divide-line divide-y">
                    {[...run.checks]
                      .sort((a, b) => Number(a.passed) - Number(b.passed))
                      .map((check) => (
                        <li key={check.name} className="py-3 text-sm">
                          <p className="font-semibold">
                            {check.passed ? 'Reported pass' : 'Not passed'} · {check.name}
                          </p>
                          <p className="text-fg-muted mt-1 leading-6">{check.detail}</p>
                        </li>
                      ))}
                  </ul>
                </details>
                {run.browserObservation ? (
                  <details className="text-fg-muted mt-2 text-sm">
                    <summary className="cursor-pointer">Browser observation receipt</summary>
                    <div className="space-y-2 pb-3 text-xs leading-5">
                      <p className="break-all">
                        Isolated replica {run.browserObservation.deploymentId} · Chromium{' '}
                        {run.browserObservation.browserVersion}
                      </p>
                      <p>
                        Identity verified before and after observation:{' '}
                        {run.browserObservation.identityVerified ? 'yes' : 'no'}
                      </p>
                      <p className="break-all">
                        Application {run.browserObservation.applicationImage}
                      </p>
                      <p className="break-all">Observer {run.browserObservation.observerImage}</p>
                      {run.browserObservation.limitations.map((limitation) => (
                        <p key={limitation}>{limitation}</p>
                      ))}
                    </div>
                  </details>
                ) : null}
              </article>
            ))
          )}
        </section>
        {snapshot.incidents.map((incident) => (
          <Incident key={incident.id} incident={incident} busy={busy} onAction={onAction} />
        ))}
        <h3 className="text-sm font-medium" data-field-tour="operations-history">
          Background operations
        </h3>
        <p className="text-fg-muted text-xs">
          Cancelling stops future results. Actions already committed remain.
        </p>
        {!snapshot.operations.length ? (
          <FieldEmpty title="No operations yet">
            Document generation, reviews, and checks keep their status here.
          </FieldEmpty>
        ) : (
          <div className="space-y-4">
            {operationGroups.map((group, index) => (
              <details key={group.title} open={index === 0}>
                <summary className="cursor-pointer text-sm font-semibold">
                  {group.title}{' '}
                  <span className="text-fg-muted ml-2 font-normal">{group.operations.length}</span>
                </summary>
                <ol className="divide-line divide-y">
                  {group.operations.map((operation) => (
                    <li key={operation.id} className="py-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium">
                          {operation.summary || operationKindLabels[operation.kind]}
                        </span>
                        <FieldStatus
                          value={
                            operation.kind === 'review' &&
                            operation.status === 'failed' &&
                            ['operation_cancelled', 'field_cancelled'].includes(
                              operation.error ?? '',
                            )
                              ? 'cancelled'
                              : operation.status
                          }
                        />
                        {operation.status === 'failed' &&
                        operation.kind === 'review' &&
                        operation.error !== 'field_cancelled' &&
                        snapshot.prs.some((pr) =>
                          pr.reviews.some(
                            (review) =>
                              review.operationId === operation.id &&
                              review.revision === pr.revision &&
                              pr.reviews
                                .filter(
                                  (item) =>
                                    item.actorId === review.actorId &&
                                    item.revision === pr.revision,
                                )
                                .at(-1)?.id === review.id,
                          ),
                        ) ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            aria-label={`Retry ${operation.summary || operationKindLabels[operation.kind]}`}
                            onClick={() =>
                              void onAction({ type: 'operation.retry', operationId: operation.id })
                            }
                          >
                            Retry
                          </Button>
                        ) : null}
                        {operation.status === 'pending' || operation.status === 'running' ? (
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={busy}
                            aria-label={`Cancel ${operation.summary || operationKindLabels[operation.kind]}`}
                            onClick={() => setCancelId(operation.id)}
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </div>
                      <p className="text-fg-muted mt-1 text-xs">
                        {operationKindLabels[operation.kind]} ·{' '}
                        <FieldTime value={operation.createdAt} />
                      </p>
                      {operation.error ? (
                        <p className="text-danger mt-2 text-sm leading-6">
                          {operation.kind === 'review'
                            ? ['operation_cancelled', 'field_cancelled'].includes(operation.error)
                              ? 'This review was cancelled. Your submitted code is still saved.'
                              : 'This review was interrupted. Open its submission to check the latest review status or retry.'
                            : operation.error}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </details>
            ))}
          </div>
        )}
      </div>
      <ConfirmationDialog
        open={cancelId !== null}
        title="Cancel this operation?"
        confirmLabel="Cancel operation"
        cancelLabel="Keep running"
        busy={busy}
        confirmDisabled={!cancellation || !['pending', 'running'].includes(cancellation.status)}
        onCancel={() => setCancelId(null)}
        onConfirm={() => {
          if (cancelId)
            void onAction({ type: 'operation.cancel', operationId: cancelId }).then((saved) => {
              if (saved) setCancelId(null);
            });
        }}
      >
        <p>{cancellation ? cancellation.summary || operationKindLabels[cancellation.kind] : ''}</p>
        <p className="mt-2">Cancelling stops future results. Actions already committed remain.</p>
      </ConfirmationDialog>
    </div>
  );
}
